<template>
  <fieldset
    v-if="!isRegistrationClosed"
    class="flex flex-col gap-y-3"
  >
    <legend
      class="mb-3 text-sm font-semibold text-gray-800"
      v-text="'Select a registration option:'"
    />
    <BaseRadioInput
      id="initial_certification_course"
      v-model="values.courseOption"
      input-value="individual"
      label="Initial Certification Course"
      name="courseOption"
    />
    <BaseRadioInput
      id="recertification_course"
      v-model="values.courseOption"
      input-value="recertification"
      label="Recertification Course"
      name="courseOption"
    />
  </fieldset>

  <slot name="price" :course-option="values.courseOption" />

  <div
    v-if="errorMessage"
    class="col-span-12 border-red bg-red-light text-gray-800 flex items-start gap-2 border-l-4 px-4 py-2 focus:outline-none max-lg:px-4"
    role="alert"
    tabindex="-1"
  >
    <div class="flex flex-col gap-2">
      <p v-html="errorMessage"></p>
    </div>
  </div>

  <button
    v-if="isCheckingStock"
    class="btn btn--primary"
    disabled
    type="button"
    v-text="'Begin Registration'"
  />
  <button
    v-else-if="!isInStock || isRegistrationClosed"
    class="btn btn--primary"
    disabled
    type="button"
    v-text="'Sold Out'"
  />
  <AppRegisterButton
    v-else
    :course-option="values.courseOption"
    :craft-entry-id="craftEntryId"
    :sku="sku"
    registration-type="course"
    button-text="Begin Registration"
  />
</template>

<script lang="ts" setup>
import { ref, onBeforeUnmount, onMounted } from 'vue'
import { useForm } from 'vee-validate'

import { getProductStockStatus } from '@js/api/products/getProductStockStatus'
import { log } from '@js/utils/logger'
import AppRegisterButton from '@js/components/checkout/AppRegisterButton.vue'
import BaseRadioInput from '@js/components/global/forms/inputs/BaseRadioInput.vue'

defineOptions({
  name: 'AppCourseRegistrationContainer',
})

const props = defineProps<{
  sku: string
  craftEntryId: number
  isRegistrationClosed: boolean
}>()

const { values } = useForm({
  initialValues: {
    courseOption: 'individual',
  },
})

const errorMessage = ref<string>('')
const isInStock = ref(false)
const isCheckingStock = ref(true)

const onAddToCartError = (e: Event) => {
  const ce = e as CustomEvent<{ message?: string }>
  errorMessage.value =
    ce.detail?.message ??
    'There was an error registering for this course. Please reach out to <a href="mailto:coursesupport@relias.com" class="underline hover:no-underline">coursesupport@relias.com</a>'
}

onMounted(async () => {
  window.addEventListener('register:addToCartError', onAddToCartError)

  try {
    const stockStatus = await getProductStockStatus(props.sku)

    if (stockStatus === null) {
      // empty items array — magento hides out-of-stock products from catalog queries
      // by default, so null is treated as the out-of-stock signal.
      // also covers visibility misconfiguration; logging for monitoring.
      log({
        message: `Stock status check returned no product for SKU "${props.sku}". Treating as out of stock. Check Magento product visibility if this is unexpected.`,
        file: 'src/wcei/js/components/course/AppCourseRegistrationContainer.vue',
      })
    }

    // only explicit IN_STOCK enables the button; null (hidden OOS) and OUT_OF_STOCK both disable it.
    isInStock.value = stockStatus === 'IN_STOCK'
  } catch (error) {
    isInStock.value = true
    log({
      message: `Failed to fetch stock status for SKU "${props.sku}". Allowing registration to proceed. Error: ${error instanceof Error ? error.message : String(error)}`,
      file: 'src/wcei/js/components/course/AppCourseRegistrationContainer.vue',
    })
  } finally {
    isCheckingStock.value = false
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('register:addToCartError', onAddToCartError)
})
</script>
